import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import apiClient from "../../services/apiClient";
import expenseMasterService from "../../services/expenseMasterService";
import expenseCategoryService from "../../services/expenseCategoryService";
import { normalizeApiError } from "../../utils/apiError";
import { formatCurrency } from "../../utils/currency";
import alertService from "../../utils/alertService";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import TextArea from "../../components/common/TextArea";
import ErrorAlert from "../../components/common/ErrorAlert";
import { encrypt } from "../../utils/encryption";
import { useMasterModals } from "../../hooks/useMasterModals";

import { MAX_FILE_SIZE, MAX_FILE_SIZE_MB } from "../../utils/constants";
import { validateFileSize, validateFileType } from "../../utils/validators";

function BLExpensePage() {
  const navigate = useNavigate();
  const { id } = useEncryptedParams();
  const basePath = "/bl-master";

  // --- States ---
  const [blData, setBLData] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    expenseId: null,
    expCatId: "",
    price: "",
    remark: "",
    file: null,
    isActive: true,
  });

  const [formErrors, setFormErrors] = useState({});

  const loadAllData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [blRes, cats, expRes] = await Promise.all([
        apiClient.get(`/bl-master/${id}`),
        expenseCategoryService.getActiveExpenseCategories(),
        expenseMasterService.getExpensesByBL(id),
      ]);

      setBLData(blRes?.data?.data || blRes?.data || {});
      setCategoryOptions(
        cats.map((c) => ({
          label: c.categoryName,
          value: c.expenseCategoryId,
        }))
      );
      setExpenses(Array.isArray(expRes) ? expRes : expRes?.data || []);
    } catch (err) {
      setError(normalizeApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const { openModal, renderModals } = useMasterModals({
    expense: loadAllData,
    expenseCategory: loadAllData
  });

  // --- Initial Load ---
  useEffect(() => {
    if (!id) {
      navigate(basePath);
      return;
    }
    loadAllData();
  }, [id, loadAllData]);

  const fetchExpenses = async () => {
    try {
      const expRes = await expenseMasterService.getExpensesByBL(id);
      setExpenses(Array.isArray(expRes) ? expRes : expRes?.data || []);
    } catch (err) {
      console.error("Failed to refresh expenses", err);
    }
  };

  // --- Handlers ---
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "price") {
      // Allow only numbers and up to 2 decimal places, and positive only
      const regex = /^\d*\.?\d{0,2}$/;
      if (value !== "" && !regex.test(value)) {
        return;
      }
      // Enforce max value
      if (value !== "" && parseFloat(value) > 10000000) {
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleFileChange = (file) => {
    const sizeError = validateFileSize(file);
    const typeError = validateFileType(file, ['application/pdf']);
    const fileError = sizeError || typeError;
    
    setFormErrors((prev) => ({ ...prev, file: fileError }));
    setFormData((prev) => ({ ...prev, file: fileError ? null : file }));
  };

  const resetForm = () => {
    setFormData({
      expenseId: null,
      expCatId: "",
      price: "",
      remark: "",
      file: null,
      isActive: true,
    });
    setFormErrors({});
  };

  const validate = () => {
    const errors = {};
    if (!formData.expCatId) errors.expCatId = "Category is required";
    if (!formData.price || parseFloat(formData.price) <= 0)
      errors.price = "Valid price is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload = { ...formData, blId: id };
      if (formData.expenseId) {
        await apiClient.patch(`/expense-master/${formData.expenseId}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        alertService.success("Expense updated successfully.");
      } else {
        await expenseMasterService.createExpense(payload);
        alertService.success("Expense added successfully.");
      }
      resetForm();
      await fetchExpenses();
    } catch (err) {
      alertService.error(normalizeApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      expenseId: item.expenseId,
      expCatId: item.expCatId,
      price: item.price,
      remark: item.remark || "",
      file: item.file || null,
      isActive: item.isActive,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (item) => {
    const res = await alertService.deleteConfirm("this expense");
    if (!res.isConfirmed) return;

    try {
      await expenseMasterService.deleteExpense(item.expenseId);
      alertService.success("Expense deleted successfully.");
      await fetchExpenses();
    } catch (err) {
      alertService.error(normalizeApiError(err).message);
    }
  };

  if (isLoading && !blData) {
    return <div className="p-10 text-center text-slate-500">Loading BL details...</div>;
  }

  const totalExpense = expenses.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);

  return (
    <div className="space-y-3 pb-8">
      {/* --- Header --- */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button
              onClick={() => navigate(basePath)}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 transition-colors"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h1 className="text-2xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
              BL <span className="text-primary-600">Expenses</span>
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[10px] sm:text-xs font-bold text-slate-500 dark:text-slate-400 md:pl-9 uppercase tracking-widest">
            <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
              BL: {blData?.blNumber || "-"}
            </span>
            <span className="hidden sm:inline">•</span>
            <span className="text-emerald-600 truncate max-w-[200px]">{blData?.vendorName || blData?.vendor?.vendorName || "-"}</span>
          </div>
        </div>
        <div className="flex items-center gap-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl px-4 py-2.5 sm:px-6 sm:py-3 shadow-sm">
          <div className="text-right">
            <p className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Total Expenses</p>
            <p className="text-lg sm:text-xl font-black text-primary-600 leading-none">{formatCurrency(totalExpense, "INR")}</p>
          </div>
        </div>
      </div>

      {error && <ErrorAlert message={error} />}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* --- Left Column: Form --- */}
        <div className="lg:col-span-4 space-y-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sticky top-6">
            <h2 className="text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight mb-6 border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 text-[10px] text-white">
                {formData.expenseId ? "✎" : "+"}
              </span>
              {formData.expenseId ? "Edit Expense" : "Add New Expense"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <SearchableDropdown
                label="Expense Category"
                name="expCatId"
                value={formData.expCatId}
                options={categoryOptions}
                error={formErrors.expCatId}
                onChange={(e) => handleInputChange({ target: { name: "expCatId", value: e.target.value } })}
                onAdd={() => openModal("expenseCategory")}
                labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
              />

              <Input
                label="Amount (INR)"
                type="text"
                inputMode="decimal"
                name="price"
                min="0.00"
                max="10000000"
                value={formData.price}
                error={formErrors.price}
                onChange={handleInputChange}
                placeholder="0.00"
                labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
              />

              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                  Bill Attachment (Optional)
                </label>
                <div className="relative group">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileChange(e.target.files[0])}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 group-hover:border-primary-500 transition-all">
                    <div className="h-8 w-8 flex items-center justify-center rounded-md bg-white dark:bg-slate-700 shadow-sm text-slate-400 group-hover:text-primary-500 transition-colors border border-slate-100 dark:border-slate-600">
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <span className="text-[11px] text-slate-500 font-bold uppercase tracking-tight">
                      {formData.file ? (typeof formData.file === 'string' ? 'Change File' : formData.file.name) : "Upload Bill/Receipt"}
                    </span>
                  </div>
                </div>
                {formErrors.file && (
                  <p className="text-[10px] font-bold text-rose-500 uppercase tracking-tight mt-1">
                    {formErrors.file}
                  </p>
                )}
              </div>

              <TextArea
                label="Remarks"
                name="remark"
                value={formData.remark}
                onChange={handleInputChange}
                rows={3}
                placeholder="Enter any additional details..."
                labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
              />

              <div className="flex items-center gap-3 pt-2">
                <Button
                  type="submit"
                  className="flex-1"
                  isLoading={isSubmitting}
                >
                  {formData.expenseId ? "Update Expense" : "Save Expense"}
                </Button>
                {formData.expenseId && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={resetForm}
                    className="text-slate-500"
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* --- Right Column: List --- */}
        <div className="lg:col-span-8 space-y-3">
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="bg-slate-50/50 dark:bg-slate-800/50 p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <h2 className="text-sm font-black text-slate-800 dark:text-white uppercase tracking-tighter flex items-center gap-3">
                Expense History
                <span className="px-2 py-0.5 rounded-full bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-[12px]">
                  {expenses.length} Records
                </span>
              </h2>
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-widest text-slate-400 font-bold bg-slate-50/30 dark:bg-slate-800/30">
                    <th className="px-6 py-4">#</th>
                    <th className="px-6 py-4">Category</th>
                    <th className="px-6 py-4 text-right">Amount</th>
                    <th className="px-6 py-4">Remarks</th>
                    <th className="px-6 py-4 text-center">Attachment</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                  {expenses.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-slate-400 italic">
                        No expenses recorded for this BL yet.
                      </td>
                    </tr>
                  ) : (
                    expenses.map((item, index) => (
                      <tr key={item.expenseId} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                        <td className="px-6 py-4 text-xs font-bold text-slate-400">{index + 1}</td>
                        <td className="px-6 py-4">
                          <span className="px-2.5 py-1 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-[11px] font-bold">
                            {item.expenseCategory?.categoryName || "General"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right font-black text-slate-800 dark:text-white">
                          {formatCurrency(item.price, "INR")}
                        </td>
                        <td className="px-6 py-4 text-xs text-slate-500 dark:text-slate-400 max-w-[200px] truncate">
                          {item.remark || "-"}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {(item.fileUrl || item.file) ? (
                            <a
                              href={item.fileUrl || item.file}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </a>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEdit(item)}
                              className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors"
                              title="Edit"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDelete(item)}
                              className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 transition-colors"
                              title="Delete"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="md:hidden divide-y divide-slate-50 dark:divide-slate-800">
              {expenses.length === 0 ? (
                <div className="px-6 py-12 text-center text-slate-400 italic">
                  No expenses recorded for this BL yet.
                </div>
              ) : (
                expenses.map((item, index) => (
                  <div key={item.expenseId} className="p-4 space-y-3 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-slate-400">#{index + 1}</span>
                        <span className="px-2.5 py-1 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-[11px] font-bold">
                          {item.expenseCategory?.categoryName || "General"}
                        </span>
                      </div>
                      <span className="font-black text-slate-800 dark:text-white">
                        {formatCurrency(item.price, "INR")}
                      </span>
                    </div>
                    
                    {item.remark && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 p-2 rounded-lg border border-slate-100 dark:border-slate-800">
                        {item.remark}
                      </p>
                    )}

                    <div className="flex items-center justify-between pt-1">
                      <div className="flex items-center gap-2">
                        {(item.fileUrl || item.file) ? (
                          <a
                            href={item.fileUrl || item.file}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-600 text-[11px] font-bold hover:bg-emerald-100 transition-colors"
                          >
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            View Bill
                          </a>
                        ) : (
                          <span className="text-[10px] font-bold text-slate-300 uppercase tracking-tighter">No Attachment</span>
                        )}
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEdit(item)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-blue-500 hover:bg-blue-50 transition-colors"
                          title="Edit"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(item)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-rose-500 hover:bg-rose-50 transition-colors"
                          title="Delete"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      {renderModals()}
    </div>
  );
}

export default BLExpensePage;
